import React, { useCallback, useMemo, useState } from "react";
import Link from "next/link";
import AssetZoom from "domains/assets/components/AssetZoom";
import { mapAssetsToImagesFiles } from "domains/file-manager/interfaces";
import useImageUploadDragDrop from "domains/image/hooks/useImageUploadDragDrop";
import ModelImageExample from "domains/models/components/ModelShow/Details/ImageExample";
import ModelShowDetailsImageFallback from "domains/models/components/ModelShow/Details/ImageFallback";
import { getModelThumbnail } from "domains/models/utils";
import useRouter from "domains/navigation/hooks/useRouter";
import { useTeamContext } from "domains/teams/contexts/TeamProvider";
import CompatibleImageUploader, {
  ImageListType,
} from "domains/ui/components/CompatibleImageUploader";
import Icon from "domains/ui/components/Icon";
import MenuItem from "domains/ui/components/Menu/MenuItem";
import ModalCrop, { ModalCropProps } from "domains/ui/components/ModalCrop";
import {
  GetAssetsByAssetIdApiResponse,
  GetModelsByModelIdApiResponse,
  GetModelsExamplesByModelIdApiResponse,
  useGetAssetsByAssetIdQuery,
} from "infra/api/generated/api";

import {
  Box,
  Center,
  Flex,
  IconButton,
  Image,
  Menu,
  MenuButton,
  MenuList,
  Spinner,
} from "@chakra-ui/react";
import { skipToken } from "@reduxjs/toolkit/dist/query";

interface ImageSectionProps {
  model: GetModelsByModelIdApiResponse["model"];
  modelExamples?: GetModelsExamplesByModelIdApiResponse;
  isUpdatingThumbnail: boolean;
  isLoadingModelExamples: boolean;
  isOnLoraCatalog?: boolean;
  onOpenAssetSelectorForThumbnail: () => void;
  onOpenAssetSelectorAndSetIndex: (index: number) => void;
  handleOnSeeMore: () => void;
  handleOnRemoveExample: (indexImage: number) => void;
  onUpdateThumbnail: (
    asset: GetAssetsByAssetIdApiResponse["asset"]
  ) => Promise<void>;
}

export const ImageSection: React.FC<ImageSectionProps> = ({
  model,
  modelExamples,
  isUpdatingThumbnail,
  isLoadingModelExamples,
  isOnLoraCatalog,
  onOpenAssetSelectorForThumbnail,
  onOpenAssetSelectorAndSetIndex,
  handleOnSeeMore,
  handleOnRemoveExample,
  onUpdateThumbnail,
}) => {
  const router = useRouter();
  const { selectedProject } = useTeamContext();
  const modelThumbnail = getModelThumbnail(model);
  const [thumbnailToCrop, setThumbnailToCrop] =
    useState<ModalCropProps["imageToCrop"]>();
  const [isUploadingThumbnail, setIsUploadingThumbnail] =
    useState<boolean>(false);
  const { currentData: modelThumbnailAsset } = useGetAssetsByAssetIdQuery(
    modelThumbnail.id
      ? {
          projectId: selectedProject.id,
          assetId: modelThumbnail.id,
        }
      : skipToken
  );

  const {
    isDraggingHover: isDraggingHoverThumbnail,
    dragFunctions,
    onDrop,
  } = useImageUploadDragDrop({ allowEventFromApp: false });

  const isModelOwned = model?.ownerId === selectedProject.id;

  const files = useMemo(() => {
    const assets = [
      ...(modelThumbnailAsset?.asset ? [modelThumbnailAsset.asset] : []),
      ...(modelExamples?.examples?.map((example) => example.asset) ?? []),
    ].filter(
      (asset, index, self) => index === self.findIndex((t) => t.id === asset.id)
    );
    return mapAssetsToImagesFiles(assets);
  }, [modelExamples?.examples, modelThumbnailAsset]);

  // ----------------------------------

  const handleUploadThumbnail = useCallback(
    async (files: ImageListType) => {
      if (files[0].dataURL !== undefined) {
        setThumbnailToCrop({
          assetId: undefined,
          src: files[0].dataURL,
        });
      }
    },
    [setThumbnailToCrop]
  );

  const handleThumbnailCropSubmit = useCallback(
    async (asset: GetAssetsByAssetIdApiResponse["asset"]) => {
      setThumbnailToCrop(undefined);
      setIsUploadingThumbnail(true);
      await onUpdateThumbnail(asset);
      setIsUploadingThumbnail(false);
    },
    [onUpdateThumbnail, setThumbnailToCrop, setIsUploadingThumbnail]
  );

  const handleThumbnailCropCancel = useCallback(() => {
    setThumbnailToCrop(undefined);
  }, [setThumbnailToCrop]);

  // ----------------------------------

  const renderModelExamples = useCallback(
    (indexes: number[]) => {
      return indexes.map((index) => {
        const assetExample = modelExamples?.examples?.[index];
        const handleOpenAssetSelectorAndSetIndex = () =>
          onOpenAssetSelectorAndSetIndex(index);
        const handleRemoveExample = () => handleOnRemoveExample(index);

        if (!assetExample) {
          return (
            <ModelShowDetailsImageFallback
              key={index}
              isModelTraining={model.status === "training"}
              onClickUploadImage={
                isLoadingModelExamples
                  ? undefined
                  : handleOpenAssetSelectorAndSetIndex
              }
              isModelOwned={isModelOwned}
            />
          );
        }

        return (
          <ModelImageExample
            key={index}
            model={model}
            assetExample={assetExample}
            imageIndex={index}
            isModelOwned={isModelOwned}
            isOnLoraCatalog={isOnLoraCatalog}
            onOpenAssetSelectorAndSetIndex={handleOpenAssetSelectorAndSetIndex}
            handleOnRemoveExample={handleRemoveExample}
            handleOnSeeMore={handleOnSeeMore}
          />
        );
      });
    },
    [
      model,
      modelExamples?.examples,
      isLoadingModelExamples,
      isModelOwned,
      isOnLoraCatalog,
      handleOnSeeMore,
      onOpenAssetSelectorAndSetIndex,
      handleOnRemoveExample,
    ]
  );

  // ----------------------------------

  return (
    <>
      <Flex flex={1} gap={2} w={"70%"} h="100%" pr={5}>
        <CompatibleImageUploader
          value={[]}
          onChange={handleUploadThumbnail}
          allowNonImageType={false}
        >
          {({ onImageUpload, dragProps: { onDrop: dragPropsOnDrop } }) => (
            <Box
              pos="relative"
              w="50%"
              {...(isModelOwned
                ? {
                    ...dragFunctions,
                    onDrop: (e) => onDrop(e, dragPropsOnDrop),
                    ...(isDraggingHoverThumbnail ? { opacity: 0.7 } : {}),
                  }
                : {})}
            >
              <Link
                href={{
                  pathname: router.pathname,
                  query: {
                    ...router.query,
                    openAssetId: modelThumbnail.id,
                  },
                }}
                style={{
                  pointerEvents: modelThumbnail.id ? "auto" : "none",
                }}
              >
                <Image
                  w="100%"
                  h="100%"
                  objectFit="cover"
                  alt="image model example"
                  aspectRatio="1"
                  rounded="lg"
                  src={modelThumbnail.url}
                />
              </Link>

              {isModelOwned && modelThumbnail && (
                <Box
                  pos="absolute"
                  top={1}
                  right={1}
                  borderRadius="md"
                  bgColor="background"
                >
                  <Menu>
                    <MenuButton as={IconButton} size={"sm"} variant="secondary">
                      <Icon id="Ui/Dots" />
                    </MenuButton>

                    <MenuList>
                      <MenuItem
                        onClick={onOpenAssetSelectorForThumbnail}
                        text="Select Thumbnail"
                      />
                      <MenuItem
                        onClick={onImageUpload}
                        text="Upload Thumbnail"
                      />
                      {!isOnLoraCatalog &&
                        modelThumbnailAsset?.asset.metadata.inferenceId && (
                          <MenuItem
                            text="Reload Parameters"
                            internalLink={
                              modelThumbnailAsset
                                ? {
                                    pathname: "/images/new",
                                    query: {
                                      importAssetId: `${model.id}|${modelThumbnailAsset?.asset.metadata.inferenceId}`,
                                      modelId: model.id,
                                    },
                                  }
                                : {}
                            }
                          />
                        )}
                    </MenuList>
                  </Menu>
                </Box>
              )}

              {(isUploadingThumbnail || isUpdatingThumbnail) && (
                <Center
                  pos="absolute"
                  top={0}
                  right={0}
                  bottom={0}
                  left={0}
                  bgColor="rgba(0,0,0,0.3)"
                >
                  <Spinner />
                </Center>
              )}
            </Box>
          )}
        </CompatibleImageUploader>

        <Flex direction="column" gap={2} w="50%">
          {[
            [0, 1],
            [2, 3],
          ].map((indexPair, pairIndex) => (
            <Flex key={pairIndex} flex={1} gap={2}>
              {renderModelExamples(indexPair)}
            </Flex>
          ))}
        </Flex>

        <AssetZoom assets={files} hasMore={false} loadMore={() => {}} />
      </Flex>

      <ModalCrop
        imageToCrop={thumbnailToCrop}
        onSubmit={handleThumbnailCropSubmit}
        onCancel={handleThumbnailCropCancel}
      />
    </>
  );
};
